/**
 * The day's movements.
 *
 * An arrival and a departure are separate rows, because they are separately
 * delayed, separately parked and often separately owned — and a turnaround
 * links them when the operator knows the pairing. That is the platform's own
 * shape (migration 0038) and this file keeps it rather than inventing a
 * friendlier one that loses the distinction.
 *
 * ── three times, never substituted ──────────────────────────────────────
 *
 *   scheduled — what the schedule says, and what the airport is judged on
 *   estimated — somebody's current belief, which is a prediction
 *   actual    — what happened, which is the only one that is evidence
 *
 * Almost every wrong number on an airport dashboard comes from one of these
 * standing in for another. A flight with an estimate and no actual has not
 * landed, however confident the estimate; a flight with neither is a flight
 * nobody has heard from. `bestKnown` exists for the one place that legitimately
 * wants "the most current time we have", and it says which of the three it
 * returned so the caller can label it.
 *
 * ── a delay is not a reason ─────────────────────────────────────────────
 *
 * The difference between scheduled and actual is arithmetic. WHY is a code
 * somebody entered, and most delays never get one. A board that shows
 * "delayed 47 minutes" beside a blank reason is telling the truth; one that
 * quietly files it under "other" has invented a fact. Unexplained delay is
 * counted and reported as its own figure, because the proportion of it is the
 * single most useful thing to know about a punctuality report.
 *
 * Pure. No React, no fetch, and `now` is always passed in.
 */
import { t, tn } from "../i18n";
import type { DelayNotice } from "./flightDelays";

export type Direction = "arrival" | "departure";

/** Where a movement came from. A forecast is never totalled with an actual. */
export type FlightSource = "schedule" | "feed" | "manual" | "forecast";

export type ServiceType = "pax" | "cargo" | "ferry" | "ga" | "military" | "positioning";

export const SERVICE_LABEL: Record<ServiceType, string> = {
  pax: "Passenger",
  cargo: "Cargo",
  ferry: "Ferry",
  ga: "General aviation",
  military: "State or military",
  positioning: "Positioning",
};

/**
 * The ICAO aerodrome reference code letter, which is what a stand is built
 * for. A is a light twin; F is an A380. The letter, not the type name, is what
 * decides whether an aircraft fits.
 */
export type SizeCode = "A" | "B" | "C" | "D" | "E" | "F";

export const SIZE_ORDER: SizeCode[] = ["A", "B", "C", "D", "E", "F"];

export function fitsIn(aircraft: SizeCode, stand: SizeCode): boolean {
  return SIZE_ORDER.indexOf(aircraft) <= SIZE_ORDER.indexOf(stand);
}

export interface Flight {
  id: string;
  direction: Direction;
  /** the operator's own designator, e.g. SV */
  carrier?: string;
  number?: string;
  registration?: string;
  /** ICAO type designator as the schedule wrote it, e.g. A320 */
  aircraftIcao?: string;
  /** the code letter the stand rules use */
  size?: SizeCode;
  /** origin for an arrival, destination for a departure */
  otherAirport?: string;
  scheduledAt?: string;
  estimatedAt?: string;
  actualAt?: string;
  /** an arrival and its departure, where the pairing is known */
  turnaround?: string;
  serviceType?: ServiceType;
  source: FlightSource;
  /** an IATA delay code, where somebody entered one */
  delayCode?: string;
  delayNote?: string;
  /** passengers aboard, where the operator files it */
  seats?: number;
  passengers?: number;
  /** each delay notice as it arrived from its source — airline, handler, ATC, feed */
  notices?: DelayNotice[];
  /** cancelled, with the reason somebody gave */
  cancelled?: { at: string; by: string; reason: string };
  /**
   * The gate this departure boards from, where the allocation placed one.
   * Preferred over the stand's own gate: a bus gate or a moved gate is the
   * flight's, not the stand's.
   */
  gate?: string;
  /**
   * Where the actual time came from, where the record says: `manual` (a
   * person entered it), `mvt`, `aidx`, `feed`. A board can then say "by hand"
   * beside a time no message reported.
   */
  actualSource?: string;
  /** who entered it, when a person did */
  actualBy?: string;
  /** a departure: the handler's committed off-block time, where one was given */
  tobt?: string;
  /** who put that commitment on the record */
  tobtBy?: string;
}

/** What a flight is called on a board: SV1234, or the registration, or nothing. */
export function flightName(f: Flight): string {
  if (f.carrier && f.number) return `${f.carrier}${f.number}`;
  if (f.number) return f.number;
  if (f.registration) return f.registration;
  return t("unidentified movement");
}

const ms = (iso: string | undefined): number | null => {
  if (!iso) return null;
  const n = Date.parse(iso);
  return Number.isNaN(n) ? null : n;
};

export type TimeKind = "actual" | "estimated" | "scheduled" | "none";

/**
 * The most current time known, and WHICH of the three it is.
 *
 * The kind comes back with it so a screen can say so. A board that shows a
 * single time column with no indication of whether it is a fact or a belief
 * is the commonest way an airport display misleads.
 */
export function bestKnown(f: Flight): { at: string | null; kind: TimeKind } {
  if (f.actualAt) return { at: f.actualAt, kind: "actual" };
  if (f.estimatedAt) return { at: f.estimatedAt, kind: "estimated" };
  if (f.scheduledAt) return { at: f.scheduledAt, kind: "scheduled" };
  return { at: null, kind: "none" };
}

/** Minutes late against the schedule; negative is early. Null when unknowable. */
export function delayMinutes(f: Flight): number | null {
  const sched = ms(f.scheduledAt);
  const when = ms(f.actualAt) ?? ms(f.estimatedAt);
  if (sched === null || when === null) return null;
  return Math.round((when - sched) / 60_000);
}

/** True only once the movement has actually happened. */
export function hasHappened(f: Flight): boolean {
  return Boolean(f.actualAt);
}

/**
 * On time, by the threshold below. PROPOSED — fifteen minutes is the figure
 * most punctuality reporting uses, and no operator here has agreed it.
 */
export const ON_TIME_MINUTES = 15;

export type Punctuality = "early" | "on_time" | "late" | "cancelled" | "unknown";

export function punctuality(f: Flight): Punctuality {
  if (f.cancelled) return "cancelled";
  const d = delayMinutes(f);
  if (d === null) return "unknown";
  if (d < -ON_TIME_MINUTES) return "early";
  return d <= ON_TIME_MINUTES ? "on_time" : "late";
}

export const PUNCTUALITY_LABEL: Record<Punctuality, string> = {
  early: "Early",
  on_time: "On time",
  late: "Late",
  cancelled: "Canceled",
  unknown: "Not known",
};

/**
 * Whether an estimate has gone stale.
 *
 * An estimate made before a scheduled time that has now passed, with no
 * actual, is not a prediction any more — it is a movement nobody has updated.
 * Boards that keep showing it as "estimated 14:20" at 15:40 are the reason
 * nobody trusts them.
 */
export function estimateStale(f: Flight, now: Date): boolean {
  if (f.actualAt || f.cancelled) return false;
  const when = ms(f.estimatedAt) ?? ms(f.scheduledAt);
  return when !== null && when < now.getTime() - 20 * 60_000;
}

/* ── the day ──────────────────────────────────────────────────────────── */

/** Local day of a movement, in the site's own timezone offset. */
export function dayOf(iso: string | undefined, offsetHours: number): string | null {
  const at = ms(iso);
  if (at === null) return null;
  return new Date(at + offsetHours * 3_600_000).toISOString().slice(0, 10);
}

export function hourOf(iso: string | undefined, offsetHours: number): number | null {
  const at = ms(iso);
  if (at === null) return null;
  return new Date(at + offsetHours * 3_600_000).getUTCHours();
}

export interface DayCounts {
  arrivals: number;
  departures: number;
  cancelled: number;
  /** movements that have happened */
  moved: number;
  /** forecast rows, kept apart from everything above */
  forecast: number;
  passengers: number | null;
  /** movements with no passenger figure, so the total is not read as complete */
  passengersUnknown: number;
}

/**
 * The day's totals.
 *
 * Forecast rows are counted separately and never added to the rest — the
 * platform's own comment on `flight.source` says a projection must never be
 * totalled alongside an actual without saying so, and this is where that
 * would otherwise happen.
 */
export function countDay(flights: readonly Flight[]): DayCounts {
  const real = flights.filter((f) => f.source !== "forecast");
  const pax = real.map((f) => f.passengers).filter((p): p is number => typeof p === "number");
  return {
    arrivals: real.filter((f) => f.direction === "arrival" && !f.cancelled).length,
    departures: real.filter((f) => f.direction === "departure" && !f.cancelled).length,
    cancelled: real.filter((f) => f.cancelled).length,
    moved: real.filter(hasHappened).length,
    forecast: flights.filter((f) => f.source === "forecast").length,
    passengers: pax.length ? pax.reduce((a, b) => a + b, 0) : null,
    passengersUnknown: real.filter((f) => f.passengers === undefined && !f.cancelled).length,
  };
}

export interface HourBar {
  hour: number;
  arrivals: number;
  departures: number;
  total: number;
}

/** Movements by hour of the local day — the shape the traffic feed asks for. */
export function byHour(flights: readonly Flight[], offsetHours: number): HourBar[] {
  const bars: HourBar[] = Array.from({ length: 24 }, (_, hour) => ({ hour, arrivals: 0, departures: 0, total: 0 }));
  for (const f of flights) {
    if (f.cancelled || f.source === "forecast") continue;
    const h = hourOf(bestKnown(f).at ?? undefined, offsetHours);
    if (h === null) continue;
    if (f.direction === "arrival") bars[h].arrivals += 1;
    else bars[h].departures += 1;
    bars[h].total += 1;
  }
  return bars;
}

/** The busiest hour, and how busy. Null when nothing is scheduled at all. */
export function peakHour(bars: readonly HourBar[]): HourBar | null {
  const busiest = [...bars].sort((a, b) => b.total - a.total)[0];
  return busiest && busiest.total > 0 ? busiest : null;
}

/* ── punctuality, and what is not explained ───────────────────────────── */

export interface PunctualityReport {
  /** movements with an actual time, which is all this can be computed from */
  measured: number;
  onTime: number;
  late: number;
  early: number;
  cancelled: number;
  /** null when nothing has happened yet: a rate over no movements is not 100% */
  onTimePct: number | null;
  /** the average of the late ones only, in minutes */
  averageDelay: number | null;
  worst: { flight: Flight; minutes: number } | null;
  /** late movements with no delay code against them */
  unexplained: number;
  /** by delay code, largest first; the code itself, uninterpreted */
  byCode: { code: string; count: number; minutes: number }[];
}

/**
 * How the day went, and how much of it nobody explained.
 *
 * Computed from movements that HAVE HAPPENED. A punctuality figure that
 * includes flights still in the air is a forecast wearing a measurement's
 * clothes, and the airport is judged on the measurement.
 */
export function punctualityOf(flights: readonly Flight[]): PunctualityReport {
  const real = flights.filter((f) => f.source !== "forecast");
  const happened = real.filter(hasHappened);
  const late = happened.filter((f) => punctuality(f) === "late");
  const delays = late.map((f) => ({ f, d: delayMinutes(f) ?? 0 }));
  const codes = new Map<string, { count: number; minutes: number }>();
  for (const { f, d } of delays) {
    if (!f.delayCode) continue;
    const entry = codes.get(f.delayCode) ?? { count: 0, minutes: 0 };
    entry.count += 1;
    entry.minutes += d;
    codes.set(f.delayCode, entry);
  }
  const worst = delays.sort((a, b) => b.d - a.d)[0];
  return {
    measured: happened.length,
    onTime: happened.filter((f) => punctuality(f) === "on_time").length,
    late: late.length,
    early: happened.filter((f) => punctuality(f) === "early").length,
    cancelled: real.filter((f) => f.cancelled).length,
    onTimePct: happened.length ? Math.round((happened.filter((f) => punctuality(f) !== "late").length / happened.length) * 100) : null,
    averageDelay: late.length ? Math.round(delays.reduce((n, x) => n + x.d, 0) / late.length) : null,
    worst: worst ? { flight: worst.f, minutes: worst.d } : null,
    unexplained: late.filter((f) => !f.delayCode).length,
    byCode: [...codes.entries()]
      .map(([code, v]) => ({ code, ...v }))
      .sort((a, b) => b.minutes - a.minutes),
  };
}

/** One line about the day's punctuality, honest about what is missing. */
export function punctualitySentence(r: PunctualityReport): string {
  if (r.measured === 0) return t("Nothing has moved yet, so there is no punctuality to report.");
  const head = t("{pct}% of {n} on time.", { pct: r.onTimePct ?? 0, n: tn(r.measured, "{n} movement", "{n} movements") });
  if (r.late === 0) return head;
  const tail =
    r.unexplained === 0
      ? t("Every late movement carries a delay code.")
      : tn(
          r.unexplained,
          "{n} late movement carries no delay code, so the reason for it is not on the record.",
          "{n} late movements carry no delay code, so the reasons for them are not on the record.",
        );
  return `${head} ${tail}`;
}
